import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ErrorService } from "@/lib/errors/ErrorService";
import type { Case, CreateCaseRequest } from "@/types/api";
import { useAuth } from "@/components/AuthProvider";

// Export Case type for backward compatibility
export type { Case };

export interface CasesPage {
  items: Case[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface UseCasesOptions {
  searchQuery?: string;
  limit?: number;
  orderBy?: 'created_at' | 'updated_at' | 'title' | 'deadline' | 'priority' | 'status';
  order?: 'ASC' | 'DESC';
}

const EMPTY_CASES_PAGE: CasesPage = {
  items: [],
  total: 0,
  limit: 50,
  offset: 0,
  hasMore: false,
};

function buildCasesEndpoint(options: Required<UseCasesOptions>, offset: number): string {
  const params = new URLSearchParams({
    limit: String(options.limit),
    offset: String(offset),
    orderBy: options.orderBy,
    order: options.order,
  });

  const trimmedSearch = options.searchQuery.trim();
  if (trimmedSearch) {
    params.set('q', trimmedSearch);
  }

  return `/cases?${params.toString()}`;
}

/**
 * Hook pour récupérer tous les dossiers du cabinet
 * Migrated to Backend API
 */
export function useCases(options: UseCasesOptions = {}) {
  const { user } = useAuth();
  const normalizedOptions: Required<UseCasesOptions> = {
    searchQuery: options.searchQuery ?? '',
    limit: options.limit ?? 50,
    orderBy: options.orderBy ?? 'created_at',
    order: options.order ?? 'DESC',
  };

  const casesQuery = useInfiniteQuery<CasesPage>({
    queryKey: ["cases", user?.id, normalizedOptions],
    queryFn: async ({ pageParam }) => {
      try {
        const offset = typeof pageParam === 'number' ? pageParam : 0;
        return await apiClient.get<CasesPage>(buildCasesEndpoint(normalizedOptions, offset));
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_cases' }, silent: true });
        return { ...EMPTY_CASES_PAGE, limit: normalizedOptions.limit };
      }
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => (
      lastPage.hasMore ? lastPage.offset + lastPage.items.length : undefined
    ),
    enabled: !!user,
    staleTime: 30000, // Cache for 30 seconds
  });

  const flattenedCases = casesQuery.data?.pages.flatMap((page) => page.items) ?? [];
  const lastPage = casesQuery.data?.pages.at(-1);

  return {
    ...casesQuery,
    data: flattenedCases,
    total: lastPage?.total ?? 0,
    loadedCount: flattenedCases.length,
    hasMore: casesQuery.hasNextPage,
    fetchMoreCases: casesQuery.fetchNextPage,
    isFetchingMore: casesQuery.isFetchingNextPage,
    isLoading: casesQuery.isLoading,
    isReady: !casesQuery.isLoading && !!user,
  };
}

/**
 * Compte serveur de dossiers, sans rapatrier les lignes.
 *
 * WHY: les compteurs d'onglets doivent refléter TOUS les dossiers du cabinet, pas
 * seulement les pages déjà chargées par le scroll infini de `useCases` — un cabinet
 * de 78 dossiers n'en a que 50 en mémoire au premier rendu, donc tout comptage
 * client-side (`cases.filter(...).length`) affiche un chiffre faux tant que
 * l'utilisateur n'a pas scrollé.
 *
 * `limit: 1` suffit : le backend calcule `total` via `COUNT(*) OVER()` sur
 * l'ensemble filtré complet (`CaseRepository.getCasesPageByCabinet`), donc la
 * requête renvoie le compte exact en ne transportant qu'une seule ligne. Le même
 * WHERE (scoping cabinet + partage) s'applique au COUNT qu'à la liste : le compte
 * ne peut pas diverger de ce que l'utilisateur a le droit de voir.
 *
 * @param options.status Filtre `cases.status` (ex. `ARCHIVED_STATUS`). Omis = tous statuts.
 * @param options.searchQuery Même recherche que la liste, pour que le compteur suive le filtre.
 */
export function useCasesCount(options: { status?: string; searchQuery?: string } = {}) {
  const { user } = useAuth();
  const status = options.status ?? '';
  const searchQuery = options.searchQuery?.trim() ?? '';

  return useQuery<number>({
    // Préfixe ["cases", ...] partagé avec `useCases` → l'invalidation `['cases']`
    // après création/archivage/suppression rafraîchit aussi les compteurs.
    queryKey: ["cases", "count", user?.id, { status, searchQuery }],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '1', offset: '0' });
      if (status) params.set('status', status);
      if (searchQuery) params.set('q', searchQuery);

      try {
        const page = await apiClient.get<CasesPage>(`/cases?${params.toString()}`);
        return page.total;
      } catch (error) {
        // `ErrorContext` est une allowlist volontairement étroite (RGPD) : ni `searchQuery`
        // (saisie utilisateur), ni l'URL construite qui la contient. Le statut compté suffit
        // à identifier le compteur fautif.
        ErrorService.handle(error, {
          context: { action: `count_cases[status=${status || 'all'}]` },
          silent: true,
        });
        return 0;
      }
    },
    enabled: !!user,
    staleTime: 30000,
  });
}

/**
 * Récupère TOUS les dossiers du cabinet en paginant (limit=200/page) jusqu'à
 * épuisement de `hasMore`.
 *
 * WHY: le backend plafonne `limit` à 200 par requête
 * (`CaseRepository.getCasesPageByCabinet`), donc une seule requête tronque les
 * cabinets de >200 dossiers. À utiliser pour les sélecteurs de dossier qui
 * filtrent côté client et doivent voir l'intégralité des dossiers — contrairement
 * à `useCases` (liste paginée pilotée par le bouton « Charger plus »). Cap de
 * 50 pages (=10 000 dossiers) comme garde-fou anti-boucle infinie.
 */
export async function fetchAllCases(): Promise<Case[]> {
  const limit = 200;
  let offset = 0;
  const all: Case[] = [];

  for (let pageIndex = 0; pageIndex < 50; pageIndex += 1) {
    const page = await apiClient.get<CasesPage>(`/cases?limit=${limit}&offset=${offset}`);
    all.push(...page.items);

    if (!page.hasMore || page.items.length === 0) {
      return all;
    }

    offset = page.offset + page.items.length;
  }

  throw new Error('Too many case pages while loading all cases (>50 pages of 200)');
}

/**
 * Hook sélecteur : charge l'intégralité des dossiers du cabinet (toutes les
 * pages). Drop-in pour les dropdowns de sélection de dossier qui filtrent côté
 * client — voir `fetchAllCases` pour le « pourquoi » de la boucle de pagination.
 */
export function useAllCases({ enabled = true }: { enabled?: boolean } = {}) {
  const { user } = useAuth();

  return useQuery<Case[]>({
    // Préfixe ["cases", ...] partagé avec `useCases` → l'invalidation `['cases']`
    // après création/édition d'un dossier rafraîchit aussi ce cache.
    queryKey: ["cases", "all", user?.id],
    queryFn: async () => {
      try {
        return await fetchAllCases();
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'fetch_all_cases' }, silent: true });
        return [];
      }
    },
    // `enabled` explicite : un écran qui AFFICHE le dossier sans le laisser
    // changer (« Déplacer vers ») n'a pas à payer la pagination complète du
    // cabinet pour un simple libellé — il le lit par `useCase`.
    enabled: !!user && enabled,
    staleTime: 30000,
  });
}

/**
 * Hook pour créer un nouveau dossier
 * Migrated to Backend API
 */
export function useCreateCase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateCaseRequest) => {
      return await apiClient.post<Case>('/cases', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cases'] });
    },
  });
}

/**
 * Hook pour rechercher des dossiers
 * Migrated to Backend API
 */
export function useSearchCases(searchTerm: string) {
  const { user } = useAuth();

  return useQuery<Case[]>({
    queryKey: ["cases", "search", searchTerm],
    queryFn: async () => {
      if (!searchTerm) return [];
      
      try {
        const cases = await apiClient.get<Case[]>(`/cases/search?q=${encodeURIComponent(searchTerm)}`);
        return cases;
      } catch (error) {
        ErrorService.handle(error, { context: { action: 'search_cases' }, silent: true });
        return [];
      }
    },
    enabled: !!user && searchTerm.length > 0,
  });
}
